import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';


export const useModelOptions = () => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithAuth('/api/models', {
        });

        if (!response.ok) {
          throw new Error('Failed to fetch models');
        }

        const data = await response.json();
        setOptions(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching model options:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [user]);

  return { options, isLoading, error };
};

export const useSystemPromptOptions = () => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithAuth('/api/system-prompts', {
        });

        if (!response.ok) {
          throw new Error('Failed to fetch system prompts');
        }

        const data = await response.json();
        setOptions(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching system prompts:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [user]);

  return { options, isLoading, error };
};

export const useOcrTierOptions = () => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      setOptions([
        { value: "standard", label: "Standard OCR", description: "Balanced performance" },
        { value: "advanced", label: "Advanced OCR", description: "Highest accuracy" }
      ]);
      return;
    }

    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithAuth('/api/documents/ocr-tiers', {
        });

        if (!response.ok) {
          throw new Error('Failed to fetch OCR tiers');
        }

        const data = await response.json();
        setOptions(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching OCR tiers:', err);
        setError(err.message);
        setOptions([
          { value: "standard", label: "Standard OCR", description: "Balanced performance" },
          { value: "advanced", label: "Advanced OCR", description: "Highest accuracy" }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [user]);

  return { options, isLoading, error };
};

export const useDefaults = () => {
  const [defaults, setDefaults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchDefaults = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithAuth('/api/defaults', {});

        if (!response.ok) {
          throw new Error('Failed to fetch defaults');
        }

        const data = await response.json();
        setDefaults(data);
      } catch (err) {
        console.error('Error fetching defaults:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaults();
  }, [user]);

  return { defaults, isLoading };
};